import './index.css'

import { XCircleIcon } from '@heroicons/react/outline'
import React, { useEffect, useRef } from 'react'

const getCopyCount = (lineWidth: number, minCopies = 2) =>
  lineWidth > 0
    ? Math.max(
        // double the copies to ensure enough text for smooth scrolling without gaps
        Math.ceil(((window.innerWidth + 100) / lineWidth) * 2),
        minCopies
      )
    : minCopies

const getDuration = (
  trackWidth: number,
  copyCount: number,
  pxPerSec: number
) => {
  const distance = trackWidth / copyCount
  return distance / pxPerSec
}

export interface ScrollingTopBannerProps {
  /**
   * The text to display in the banner, separated by a dot (•)
   */
  bannerText: string[]
  /**
   * The speed at which the banner scrolls (in pixels per second)
   */
  scrollSpeed?: number
  /**
   * Optional click handler
   */
  onClick?: () => void
  /**
   * Called when close button is pressed
   */
  onClose?: () => void
  /** Optional to hide the close button (default: false)
   */
  hideCloseButton?: boolean
}

/**
 * A scrolling top banner component that displays a message at the top of the page.
 * Dynamically calculates the number of copies of the text needed to fill the width of the screen, and adjusts the scrolling duration accordingly.
 */
export const ScrollingTopBanner = ({
  bannerText,
  scrollSpeed = 30,
  onClick,
  onClose,
  hideCloseButton = false,
}: ScrollingTopBannerProps) => {
  const trackRef = useRef<HTMLDivElement | null>(null)
  const lineRef = useRef<HTMLDivElement | null>(null)
  const [textCopyCount, setTextCopyCount] = React.useState<number>(2)

  useEffect(() => {
    const el = trackRef.current
    const line = lineRef.current
    if (!el || !line) return

    const updateBanner = () => {
      const lineWidth = line.scrollWidth
      const copyCount = getCopyCount(lineWidth)
      const duration = getDuration(el.scrollWidth, copyCount, scrollSpeed)
      el.style.setProperty('--marquee-duration', `${duration}s`)
      el.style.setProperty('--translate-percent', `-${100 / copyCount}%`)
      setTextCopyCount(copyCount)
    }

    updateBanner()
    const ro = new ResizeObserver(updateBanner)
    ro.observe(el)
    const id = window.setTimeout(updateBanner, 100)

    return () => {
      ro.disconnect()
      window.clearTimeout(id)
    }
  }, [scrollSpeed, bannerText])

  const renderCopy = (prefix: string) =>
    bannerText.flatMap((v, i) => [
      <div
        key={`${prefix}-item-${i}`}
        className="flex select-none items-center gap-6"
      >
        <p>{v}</p>
        <div className="h-[6px] w-[6px] rounded-full bg-white" />
      </div>,
      <div key={`${prefix}-sp-${i}`} className="w-6 shrink-0" />,
    ])

  return (
    <div
      id="scrolling-top-banner"
      className="flex w-full cursor-pointer overflow-hidden bg-[#1a1a1a]"
      onClick={onClick}
    >
      <div
        className={
          'flex w-full items-center py-3 text-lg text-white lg:text-xl'
        }
      >
        <div className="flex-1 overflow-hidden">
          <div
            ref={trackRef}
            className="text-nowrap marquee-hover-pause whitespace-nowrap uppercase italic"
          >
            <div className="marquee-inner inline-flex items-center will-change-transform">
              <div ref={lineRef} className="flex">
                {renderCopy('copy0')}
              </div>
              {Array.from({ length: textCopyCount - 1 }).map((_, idx) => (
                <div key={`copy-${idx}`} className="flex">
                  {renderCopy(`copy${idx}`)}
                </div>
              ))}
            </div>
          </div>
        </div>
        {!hideCloseButton && (
          <button
            aria-label="Close banner"
            className="shrink-0 px-2 hover:opacity-80"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onClose?.()
            }}
          >
            <XCircleIcon className="h-5 w-5 stroke-white" />
          </button>
        )}
      </div>
    </div>
  )
}
